import { views, viewToDomElement, ensureViewsLoaded } from '../../common/views';
import { submitInsightFeedback } from './support-insights-api';

/**
 * RecommendationModalManager - Handles AI recommendation modal display and interactions
 */
export class RecommendationModalManager {
  constructor(shadowRoot) {
    this.root = shadowRoot;
    this.listenersInit = false;
    this.escapeHandler = null;
    this.responseData = null;
  }

  $(selector) {
    return this.root.querySelector(selector);
  }

  /**
   * Inject the modal HTML into the DOM
   */
  async inject() {
    if (this.$('#recommendation-modal-overlay')) return;
    await ensureViewsLoaded(['recommendation-modal']);
    const render = views.get('recommendation-modal');
    if (!render) return;
    this.root.appendChild(viewToDomElement(render()));
  }

  /**
   * Set AI agent response data for the modal
   */
  setData(data) {
    this.responseData = data;
  }

  /**
   * Initialize click delegation for view details and expand buttons
   */
  initClickDelegation() {
    this.root.addEventListener('click', (e) => {
      const target = e.target;

      // Handle View Details button
      const viewDetailsBtn = target?.closest('.view-details-btn');
      if (viewDetailsBtn) {
        e.preventDefault();
        this.open();
        return;
      }

      // Handle Expand button
      const expandBtn = target?.closest('#recommendation-modal-expand-btn');
      if (expandBtn) {
        e.preventDefault();
        const wrapper = this.$('.recomend-container-wrapper');
        wrapper?.classList.toggle('is-expanded');
        return;
      }
    });
  }

  /**
   * Initialize modal listeners (only once)
   */
  initListeners() {
    const overlay = this.$('#recommendation-modal-overlay');
    const downloadBtn = this.$('#recommendation-modal-download');
    const closeFooterBtn = this.$('#recommendation-modal-close-btn');
    const adobeTab = this.$('#recommendation-tab-adobe');
    const publicTab = this.$('#recommendation-tab-public');

    overlay?.addEventListener('click', (e) => {
      if (e.target.id === 'recommendation-modal-overlay') this.close();
    });

    closeFooterBtn?.addEventListener('click', () => this.close());

    downloadBtn?.addEventListener('click', () => {
      console.log('Recommendation Download clicked', { data: this.responseData });
      // TODO: Implement download functionality
    });

    adobeTab?.addEventListener('click', () => this.switchTab('adobe'));
    publicTab?.addEventListener('click', () => this.switchTab('public'));

    this.initFeedbackListeners();

    this.escapeHandler = (e) => {
      const overlay = this.$('#recommendation-modal-overlay');
      if (e.key === 'Escape' && !overlay?.classList.contains('is-hidden')) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
  }

  /**
   * Initialize feedback form listeners
   */
  initFeedbackListeners() {
    const feedbackForm = this.$('#recommendation-feedback-form');
    const feedbackThanks = this.$('#recommendation-feedback-thanks');
    const feedbackButtons = this.$('#recommendation-modal-feedback-buttons');
    const feedbackUp = this.$('#recommendation-feedback-up');
    const feedbackDown = this.$('#recommendation-feedback-down');
    const feedbackSubmit = this.$('#recommendation-feedback-submit');
    const feedbackDismiss = this.$('#recommendation-feedback-dismiss');
    const feedbackTextarea = this.$('#recommendation-feedback-textarea');

    let feedbackType = null;

    const showFeedbackForm = (type) => {
      feedbackType = type;
      if (feedbackForm) {
        feedbackForm.classList.remove('is-hidden');
        feedbackThanks?.classList.add('is-hidden');
        if (type === 'up') {
          feedbackUp?.classList.add('selected');
          feedbackDown?.classList.remove('selected');
        } else {
          feedbackDown?.classList.add('selected');
          feedbackUp?.classList.remove('selected');
        }
      }
    };

    const hideFeedbackForm = () => {
      if (feedbackForm) {
        feedbackForm.classList.add('is-hidden');
        feedbackUp?.classList.remove('selected');
        feedbackDown?.classList.remove('selected');
        if (feedbackTextarea) feedbackTextarea.value = '';
      }
    };

    const showThanksMessage = () => {
      hideFeedbackForm();
      feedbackButtons?.classList.add('is-hidden');
      feedbackThanks?.classList.remove('is-hidden');
    };

    const setSubmittingState = (submitting) => {
      const submitLabel = feedbackSubmit?.getAttribute('data-submit-text') || 'Submit';
      const submittingLabel = feedbackSubmit?.getAttribute('data-submitting-text') || 'Submitting...';
      if (feedbackSubmit) {
        feedbackSubmit.disabled = submitting;
        feedbackSubmit.setAttribute('aria-busy', submitting ? 'true' : 'false');
        feedbackSubmit.textContent = submitting ? submittingLabel : submitLabel;
        submitting ? feedbackSubmit.classList.add('is-submitting') : feedbackSubmit.classList.remove('is-submitting');
      }
      if (feedbackDismiss) {
        feedbackDismiss.disabled = submitting;
      }
      if (!submitting) {
        updateSubmitDisabledFromTextarea();
      }
    };

    const resetSubmitState = () => {
      setSubmittingState(false);
    };

    const updateSubmitDisabledFromTextarea = () => {
      if (feedbackSubmit?.classList.contains('is-submitting')) return;
      const hasText = (feedbackTextarea?.value?.trim() || '').length > 0;
      if (feedbackSubmit) feedbackSubmit.disabled = !hasText;
    };

    feedbackUp?.addEventListener('click', () => {
      showFeedbackForm('up');
      updateSubmitDisabledFromTextarea();
    });
    feedbackDown?.addEventListener('click', () => {
      showFeedbackForm('down');
      updateSubmitDisabledFromTextarea();
    });
    feedbackTextarea?.addEventListener('input', updateSubmitDisabledFromTextarea);
    feedbackDismiss?.addEventListener('click', () => {
      if (!feedbackDismiss?.disabled) hideFeedbackForm();
    });
    feedbackSubmit?.addEventListener('click', async () => {
      if (feedbackSubmit?.disabled) return;
      const feedback = feedbackTextarea?.value || '';
      setSubmittingState(true);
      try {
        const activeTab=this.root.querySelector('.recommendation-tab.is-active');
        let activeTabData='adobe';
        if(activeTab){
          activeTabData=activeTab.dataset.tab;
        }
        
        const isFeedbackSubmitted = await submitInsightFeedback({
          type: feedbackType,
          feedback,
          insightTitle: activeTabData==='adobe'?'Recommedation - Adobe Documentation':'Recommedation - Public Documentation',
          root: this.root
        });
        if (isFeedbackSubmitted) {
          showThanksMessage();
        } else {
          console.error('error in submitting feedback');
          resetSubmitState();
        }
      } catch (err) {
        console.error('error in submitting feedback', err);
        resetSubmitState();
      }
    });
  }

  /**
   * Switch between adobe and public tabs
   */
  switchTab(tab) {
    const adobeTab = this.$('#recommendation-tab-adobe');
    const publicTab = this.$('#recommendation-tab-public');

    if (tab === 'adobe') {
      adobeTab?.classList.add('is-active');
      publicTab?.classList.remove('is-active');
    } else {
      publicTab?.classList.add('is-active');
      adobeTab?.classList.remove('is-active');
    }

    this.populate(tab);
  }

  /**
   * Render markdown text to HTML
   */
  createMarkdown(elementId, text) {
    try {
      const markdownText = text ? text.replace(/\\n/g, '\n') : '';
      const md = window.markdownit({
        html: true,
        linkify: true,
        typographer: true
      });

      const result = md.render(markdownText);
      const element = this.$(elementId);
      
      if (element) {
        element.innerHTML = result || '<p>No recommendations available.</p>';
      }

      return true;
    } catch (error) {
      console.error('Error rendering markdown:', error);
      const element = this.$(elementId);
      
      if (element) {
        element.innerHTML = text || '<p>No recommendations available.</p>';
      }

      return false;
    }
  }

  /**
   * Populate modal content based on tab type
   */
  populate(type = 'adobe') {
    const basedOnList = this.$('#recommendation-based-on-list');

    if (!this.responseData) {
      this.createMarkdown('#recommendation-modal-content', '');
      return;
    }

    const source = type === 'adobe' 
      ? this.responseData.adobeSource 
      : this.responseData.publicSource;
    const refs = type === 'adobe' 
      ? this.responseData.adobeReferences 
      : this.responseData.publicReferences;

    this.createMarkdown('#recommendation-modal-content', source);

    if (basedOnList) {
      if (refs && refs.length > 0) {
        basedOnList.innerHTML = refs.map(link => 
          `<li><a href="${link}" target="_blank">${link}</a></li>`
        ).join('');
        basedOnList.parentElement.style.display = 'block';
      } else {
        basedOnList.parentElement.style.display = 'none';
      }
    }
  }

  /**
   * Open the recommendation modal
   */
  open(type = 'adobe') {
    const overlay = this.$('#recommendation-modal-overlay');
    if (!overlay || !this.responseData) {
      console.warn('Recommendation modal or data not available');
      return;
    }

    if (!this.listenersInit) {
      this.initListeners();
      this.listenersInit = true;
    }

    this.switchTab(type);
    overlay.classList.remove('is-hidden');
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close the recommendation modal
   */
  close() {
    const overlay = this.$('#recommendation-modal-overlay');
    overlay?.classList.add('is-hidden');
    document.body.style.overflow = '';
  }
}

export const createRecommendationModalManager = (root) => new RecommendationModalManager(root);
export default RecommendationModalManager;

